import { SITE_ORIGIN, HT } from "../siteConfig";
import SEO from "../components/seo.jsx"
import { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// ── Swap these asset imports per page ──
import HeroImg       from "../assets/topical-breadcrumb-bg.png";
import BreadcrumbImg from "../assets/topical-breadcrumb-bg.jpg";
import SurgeonImg    from "../assets/drbhaveshgupta.jpeg";
import ProcedureImg  from "../assets/topical-procedure.jpg";
import ResultImg     from "../assets/topical-result.jpg";

gsap.registerPlugin(ScrollTrigger);
const cinzel = { fontFamily: "'Cinzel', serif" };

/* ============================================================
   PAGE DATA
============================================================ */
const PAGE = {
  hero: {
    eyebrow: "Medical Hair Therapy",
    heading: "Topical & Oral Therapy for Hair Loss",
    sub: "FDA-approved Minoxidil, Finasteride and Dutasteride, combined for maximum, lasting results at Clinique Internationale.",
  },

  breadcrumb: [
    { label: "Home", href: "/" },
    { label: "Treatments", href: "/topical-therapy.php" },
    { label: "Topical & Oral Therapy", href: "/topical-therapy.php" },
  ],

  /* ── INTRO ── */
  intro: {
    eyebrow: "Overview",
    heading: "What is Topical & Oral Medical Therapy?",
    paragraphs: [
      "Minoxidil, Finasteride and Dutasteride are the FDA-approved medicines for the management of hair loss, backed by decades of clinical use and proven results.",
      "At Clinique Internationale, we combine your hair transplant, hair vitamins, and these medications in a way that extracts maximum advantage from all of them together achieving lustrous, sustained hair growth for our patients.",
      "Used correctly and under supervision, this medical foundation doesn't just slow hair loss it actively supports the results of any surgical or nutritional treatment you undergo alongside it.",
    ],
  },

  /* ── SCIENCE ── */
  science: {
    eyebrow: "The Science",
    heading: "How These Medications Work",
    intro: "Each medication targets hair loss through a different mechanism together, they address both the vascular and hormonal drivers of thinning hair.",
    growthFactors: [
      { code: "MXD", name: "Minoxidil (Topical)",     desc: "A vasodilator that increases blood flow to hair follicles and prolongs the active growth (anagen) phase of the hair cycle." },
      { code: "FIN", name: "Finasteride (Oral)",       desc: "A Type II 5-alpha reductase inhibitor that lowers scalp DHT levels, the hormone primarily responsible for pattern hair loss." },
      { code: "DUT", name: "Dutasteride (Oral)",       desc: "Blocks both Type I and Type II 5-alpha reductase, offering broader and often stronger DHT suppression than Finasteride." },
      { code: "COM", name: "Combination Synergy",      desc: "Using these together tackles hair loss from multiple angles simultaneously, rather than relying on a single mechanism." },
    ],
    mechanisms: [
      { num: "01", title: "Increased Blood Flow",   desc: "Minoxidil widens blood vessels at the scalp, improving oxygen and nutrient delivery to weakened follicles." },
      { num: "02", title: "DHT Suppression",        desc: "Finasteride and Dutasteride reduce the hormone that causes follicles to shrink and eventually stop producing hair." },
      { num: "03", title: "Anagen Prolongation",     desc: "Follicles spend more time in the active growth phase, resulting in thicker, longer-lasting strands." },
      { num: "04", title: "Combined Protocol",      desc: "Medication is layered with vitamins and, where appropriate, hair transplant surgery for compounding results." },
    ],
  },

  /* ── SURGEON ── */
  surgeon: {
    eyebrow: "Lead Specialist",
    heading: "Medical Hair Therapy at Clinique Internationale",
    paragraphs: [
      "At Clinique Internationale, medical hair loss management is designed and monitored by Dr. Bhavesh Gupta, who evaluates each patient's hair loss pattern, medical history, and goals before prescribing any combination of Minoxidil, Finasteride, or Dutasteride.",
      "Dr. Gupta integrates this medical foundation with cyclic vitamin therapy and, where relevant, hair transplant surgery building a coordinated plan rather than treating each option in isolation.",
    ],
    tags: [ "Advanced Fellowship – Cosmetic Surgery", "AOSMI Member", "15+ Yrs Experience", "10,000+ Cases"],
  },

  /* ── COST ── */
  cost: {
    eyebrow: "Pricing",
    heading: `Topical & Oral Therapy Cost in ${HT.placeName}`,
    paragraphs: [
      "Cost depends on which medications are prescribed and the treatment duration. A typical monthly course, inclusive of consultation and monitoring, ranges from ₹1,500 to ₹4,000.",
    ],
    cities: [
      { city: "Mumbai",    range: "₹2,000 – ₹4,000 / month", href: "/",      surgeon: "Dr. Bhavesh Gupta"  },
      { city: "Bangalore", range: "₹1,500 – ₹3,500 / month", href: "https://bangalore.hairclinique.in/",              surgeon: "Dr. Idris MD"          },
      { city: "Pune",      range: "₹1,500 – ₹3,500 / month", href: "https://pune.hairclinique.in/", surgeon: "Dr. Ajinkya Patil"  },
      { city: "Shirdi",    range: "₹1,500 – ₹3,500 / month", href: "https://shirdi.hairclinique.in/", surgeon: "Dr. Pranay Thakur"    }
    ],
  },

  /* ── PROCEDURE STEPS ── */
  procedure: {
    eyebrow: "The Protocol",
    heading: "How the Treatment Plan is Built",
    intro: "Medication is never prescribed without an assessment every plan is matched to the patient's hair loss pattern and health profile.",
    steps: [
      { num: "01", title: "Diagnosis & History",   desc: "Dr. Gupta assesses the hair loss pattern, family history, and any contraindications before prescribing." },
      { num: "02", title: "Medication Selection",  desc: "The right combination Minoxidil, Finasteride, and/or Dutasteride is chosen for the individual case." },
      { num: "03", title: "Dosage Titration",      desc: "Dosage is started conservatively and adjusted based on tolerance and response over the following weeks." },
      { num: "04", title: "Integration Planning",  desc: "Vitamins, and hair transplant surgery if planned, are layered into the same coordinated protocol." },
      { num: "05", title: "Progress Monitoring",   desc: "Regular follow-ups track shedding, density, and any side effects, with adjustments made as needed." },
      { num: "06", title: "Long-Term Maintenance", desc: "Once results stabilise, the patient moves to a maintenance dose to sustain hair growth long-term." },
    ],
  },

  /* ── BENEFITS ── */
  benefits: {
    eyebrow: "Why Choose This Therapy",
    heading: "Benefits of Combined Medical Therapy",
    items: [
      { title: "FDA-Approved",        desc: "Minoxidil, Finasteride and Dutasteride are backed by extensive clinical research and regulatory approval." },
      { title: "Multi-Angle Action",  desc: "Combining medications addresses both blood flow and hormonal causes of hair loss at the same time." },
      { title: "Boosts Surgical Results", desc: "Medical therapy protects existing hair and supports newly transplanted grafts for a fuller overall outcome." },
      { title: "Fully Customisable", desc: "Dosage and combination are tailored to each patient, rather than a one-size-fits-all prescription." },
      { title: "Non-Invasive",        desc: "No procedures required medication fits easily alongside any other treatment you're already undergoing." },
    ],
  },

  /* ── WHO IS A CANDIDATE ── */
  candidates: {
    eyebrow: "Is This Right for You?",
    heading: "Who is a Good Candidate?",
    ideal: [
      "Men with early to moderate androgenetic (male pattern) hair loss",
      "Patients looking to protect existing hair before or after a hair transplant",
      "Individuals wanting a long-term, medically managed approach to hair loss",
      "Those open to combining medication with vitamins or surgical treatment",
    ],
    notIdeal: [
      "Women who are pregnant, breastfeeding, or planning to conceive",
      "Patients with certain liver conditions, without prior physician clearance",
      "Individuals unwilling to commit to consistent, long-term use",
    ],
  },

  /* ── FAQs ── */
  faqs: [
    {
      q: "How long before I see results?",
      a: "Most patients notice reduced shedding within 2–3 months, with visible improvement in density typically apparent after 6–12 months of consistent use.",
    },
    {
      q: "Is Finasteride or Dutasteride safe for long-term use?",
      a: "Yes, under medical supervision. Dr. Gupta monitors patients regularly and adjusts the protocol based on response and tolerance.",
    },
    {
      q: "Can this be combined with a hair transplant?",
      a: "Yes and it's strongly recommended. Medical therapy protects both existing hair and transplanted grafts, supporting a fuller, longer-lasting result.",
    },
    {
      q: "What happens if I stop the medication?",
      a: "Hair loss can gradually resume once medication is stopped, since the underlying hormonal and vascular factors return to their untreated state. Long-term use sustains results.",
    },
  ],

  /* ── CTA ── */
  cta: {
    eyebrow: "Take the First Step",
    heading: "Start Your Medical Hair Therapy Plan",
    sub: "Book a consultation with our specialists at Clinique Internationale to receive a personalised medication plan built around your hair loss pattern and goals.",
    primary:   { label: "Book Consultation", href: "https://api.whatsapp.com/send?phone=919082123009&text=I%27m%20interested%20in%20Topical%20%26%20Oral%20Therapy%20for%20Hair%20Loss" },
    secondary: { label: "View All Treatments", href: "/topical-therapy.php" },
  },
};

/* ============================================================
   REUSABLE SUB-COMPONENTS  (identical across all treatment pages)
============================================================ */
function Eyebrow({ children, light = false, align = "left" }) {
  return (
    <p style={cinzel} className={`text-[10.5px] tracking-[0.28em] uppercase mb-3 flex items-center gap-2.5 ${align === "center" ? "justify-center" : "justify-start"} ${light ? "text-[#c9a84c]/70" : "text-[#a07828]"}`}>
      <span className={`w-6 h-px ${light ? "bg-[#c9a84c]/50" : "bg-[#c9a84c]"}`} />
      {children}
    </p>
  );
}

function SectionHeading({ children, light = false, align = "left" }) {
  return (
    <h2 style={cinzel} className={`font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.85rem] uppercase tracking-[0.05em] mb-8 leading-tight ${align === "center" ? "text-center" : "text-left"} ${light ? "text-white" : "text-[#1a0f02]"}`}>
      {children}
    </h2>
  );
}

function GoldDivider() {
  return <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />;
}

function FramedImage({ src, alt, className = "" }) {
  return (
    <div className={`relative rounded-2xl overflow-hidden border border-[#c9a84c]/25 ${className}`}>
      <img src={src} alt={alt} className="w-full h-full object-cover" />
      <span className="absolute inset-0 ring-1 ring-inset ring-[#c9a84c]/15 rounded-2xl pointer-events-none" />
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-xl overflow-hidden transition-colors duration-300 ${open ? "border-[#c9a84c]/40 bg-[#c9a84c]/[0.04]" : "border-[#c9a84c]/15 bg-white"}`}>
      <button className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-5 text-left" onClick={() => setOpen(p => !p)}>
        <span style={cinzel} className="text-[13px] font-bold tracking-[0.04em] text-[#1a0f02]">{q}</span>
        <span className={`flex-shrink-0 w-7 h-7 rounded-full border border-[#c9a84c]/35 flex items-center justify-center transition-transform duration-300 ${open ? "rotate-45 bg-[#c9a84c] border-[#c9a84c]" : ""}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 2v8M2 6h8" stroke={open ? "#1a0f02" : "#c9a84c"} strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      {open && <div className="px-5 sm:px-6 pb-5"><p className="text-[13.5px] leading-[1.8] text-[#6b5535]">{a}</p></div>}
    </div>
  );
}

/* ============================================================
   PAGE COMPONENT
============================================================ */
export default function TopicalOralTherapy() {
  const pageRef     = useRef(null);
  const heroEyeRef  = useRef(null);
  const heroHeadRef = useRef(null);
  const heroSubRef  = useRef(null);
  const secRefs     = useRef([]);
  const addSection  = (el) => el && !secRefs.current.includes(el) && secRefs.current.push(el);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";
      gsap.fromTo(heroEyeRef.current,  { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7,  ease });
      gsap.fromTo(heroHeadRef.current, { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.75, ease, delay: 0.1  });
      gsap.fromTo(heroSubRef.current,  { opacity: 0, y: 22 }, { opacity: 1, y: 0, duration: 0.7,  ease, delay: 0.18 });
      secRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(el, { opacity: 0, y: 36 }, {
          opacity: 1, y: 0, duration: 0.75, ease,
          scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none reverse" },
        });
      });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  const wrap = "w-full px-5 sm:px-10 lg:px-16 xl:px-24";
  const sec  = "py-16 sm:py-20 md:py-24";

  return (
    <>
    <SEO
  title="Topical & Oral Therapy for Hair Loss | Clinique Internationale"
  description="Effective topical and oral therapy for hair loss at Clinique Internationale, led by Dr. Bhavesh Gupta. Medically guided treatments for lasting results."
  url={`${SITE_ORIGIN}/topical-therapy.php`}
/>
      <link href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap" rel="stylesheet" />

<div ref={pageRef} className="relative w-full bg-[#f2ece0] overflow-hidden mt-16 lg:mt-20" style={{ fontFamily: "'Raleway', sans-serif" }}>

  {/* ══════════════════════════════════════
      HERO
  ══════════════════════════════════════ */}
  <div className="relative w-full h-[200px] xs:h-[280px] sm:h-[340px] md:h-[700px] flex items-center justify-end overflow-hidden">
    <img className="absolute inset-0 w-full h-full object-cover object-[center_20%] md:object-center" src={HeroImg} alt={PAGE.hero.heading} />
    <div className="absolute inset-0 " />
    <div className="absolute inset-0 opacity-40 pointer-events-none"
      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")" }}
    />
    <div className="relative z-10 text-right px-4 sm:px-10 md:px-16 max-w-[85%] sm:max-w-xl ml-auto mr-0">
      <p ref={heroEyeRef} style={cinzel} className="text-[8px] xs:text-[9px] sm:text-[10.5px] tracking-[0.2em] xs:tracking-[0.24em] sm:tracking-[0.32em] uppercase text-black mb-2 sm:mb-4 flex items-center justify-end gap-2 sm:gap-3">
        <span className="w-4 sm:w-7 h-px bg-black" />{PAGE.hero.eyebrow}<span className="w-4 sm:w-7 h-px bg-black" />
      </p>
      <h1 ref={heroHeadRef} style={cinzel} className="font-black text-[1.4rem] xs:text-[1.8rem] sm:text-[2.8rem] md:text-[3.4rem] tracking-[0.02em] sm:tracking-[0.04em] uppercase text-black mb-2 sm:mb-3 leading-[1.15] sm:leading-tight">
        {PAGE.hero.heading}
      </h1>
      <p ref={heroSubRef} className="text-[11px] xs:text-[12px] sm:text-[15px] text-black/70 leading-relaxed ml-auto">{PAGE.hero.sub}</p>
    </div>
  </div>


        {/* ── BREADCRUMB ── */}
        <div className="relative w-full overflow-hidden">
          <img className="absolute inset-0 w-full h-full object-cover" src={BreadcrumbImg} alt="" aria-hidden="true" />
          <div className="absolute inset-0 bg-[#0e0905]/88" />
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/25 to-transparent" />
          <div className={`relative ${wrap} py-3.5 flex flex-wrap items-center gap-2`}>
            {PAGE.breadcrumb.map((b, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <span className="text-[#c9a84c]/30 text-xs">›</span>}
                {b.href
                  ? <a href={b.href} style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]/60 hover:text-[#c9a84c] transition-colors">{b.label}</a>
                  : <span style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]">{b.label}</span>}
              </span>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════
            INTRO
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="max-w-4xl mx-auto text-center">
            <Eyebrow align="center">{PAGE.intro.eyebrow}</Eyebrow>
            <SectionHeading align="center">{PAGE.intro.heading}</SectionHeading>
            {PAGE.intro.paragraphs.map((p, i) => (
              <p key={i} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] mb-4">{p}</p>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════
            SCIENCE medications + mechanisms (dark band)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow light align="center">{PAGE.science.eyebrow}</Eyebrow>
              <SectionHeading light align="center">{PAGE.science.heading}</SectionHeading>
              <p className="text-[14px] leading-[1.85] text-white/55 max-w-3xl mx-auto text-center mb-12">{PAGE.science.intro}</p>

              <p style={cinzel} className="text-[10px] tracking-[0.22em] uppercase text-[#c9a84c]/60 text-center mb-5">FDA-Approved Medications</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-14">
                {PAGE.science.growthFactors.map((g) => (
                  <div key={g.code} className="relative overflow-hidden bg-white/[0.04] border border-[#c9a84c]/15 rounded-2xl p-5 hover:bg-[#c9a84c]/[0.08] hover:border-[#c9a84c]/30 transition-colors text-center">
                    <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/35 to-transparent" />
                    <div style={cinzel} className="font-black text-[1.5rem] leading-none mb-1 bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a] bg-clip-text text-transparent">{g.code}</div>
                    <div style={cinzel} className="text-[9px] tracking-[0.1em] uppercase text-[#c9a84c]/70 mb-3 leading-snug">{g.name}</div>
                    <p className="text-[11.5px] leading-[1.7] text-white/50">{g.desc}</p>
                  </div>
                ))}
              </div>

              <p style={cinzel} className="text-[10px] tracking-[0.22em] uppercase text-[#c9a84c]/60 text-center mb-5">Mechanisms of Action</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                {PAGE.science.mechanisms.map((m) => (
                  <div key={m.num} className="relative overflow-hidden bg-gradient-to-br from-[#c9a84c]/[0.07] to-[#c9a84c]/[0.02] border border-[#c9a84c]/18 rounded-2xl p-6 hover:border-[#c9a84c]/35 transition-colors">
                    <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/35 to-transparent" />
                    <span style={cinzel} className="font-black text-[2rem] leading-none text-[#c9a84c]/18 select-none block mb-3">{m.num}</span>
                    <div style={cinzel} className="text-[12px] font-bold tracking-[0.06em] uppercase text-white/85 mb-2">{m.title}</div>
                    <p className="text-[12.5px] leading-[1.75] text-white/50">{m.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            SURGEON
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <FramedImage src={SurgeonImg} alt="Dr. Bhavesh Gupta reviewing a medical hair therapy plan" className="w-full h-auto object-contain" />
            <div>
              <Eyebrow>{PAGE.surgeon.eyebrow}</Eyebrow>
              <SectionHeading>{PAGE.surgeon.heading}</SectionHeading>
              {PAGE.surgeon.paragraphs.map((p, i) => (
                <p key={i} className="text-[14px] leading-[1.9] text-[#3a2e1e] mb-4">{p}</p>
              ))}
              <div className="flex flex-wrap gap-2 mt-5">
                {PAGE.surgeon.tags.map(t => (
                  <span key={t} style={cinzel} className="text-[9.5px] tracking-[0.12em] uppercase text-[#a07828] border border-[#c9a84c]/30 rounded px-3 py-1">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            COST + CITY CARDS
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#faf8f4] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow align="center">{PAGE.cost.eyebrow}</Eyebrow>
              <SectionHeading align="center">{PAGE.cost.heading}</SectionHeading>
              {PAGE.cost.paragraphs.map((p, i) => (
                <p key={i} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] max-w-3xl mx-auto text-center mb-4">{p}</p>
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto mt-8">
                {PAGE.cost.cities.map((c, i) => (
                  <a key={i} href={c.href}
                    className="bg-white border border-[#c9a84c]/18 rounded-2xl p-6 relative overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_12px_32px_rgba(90,60,10,0.1)] transition-all duration-300 block text-center group">
                    <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                    <div style={cinzel} className="text-[13px] font-bold tracking-[0.06em] uppercase text-[#1a0f02] mb-1 group-hover:text-[#a07828] transition-colors">{c.city}</div>
                    <div className="text-[11.5px] text-[#6b5535] mb-3">{c.surgeon}</div>
                    <div style={cinzel} className="font-black text-[1rem] leading-snug bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a] bg-clip-text text-transparent">{c.range}</div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            PROCEDURE STEPS (image + 2-col grid)
        ══════════════════════════════════════ */}
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow light align="center">{PAGE.procedure.eyebrow}</Eyebrow>
              <SectionHeading light align="center">{PAGE.procedure.heading}</SectionHeading>
              <p className="text-[14px] leading-[1.9] text-white/55 max-w-3xl mx-auto text-center mb-10">{PAGE.procedure.intro}</p>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
                <FramedImage src={ProcedureImg} alt="Medical hair therapy consultation" className="h-[260px] sm:h-[340px] lg:h-full lg:sticky lg:top-24" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {PAGE.procedure.steps.map((s) => (
                    <div key={s.num} className="relative overflow-hidden bg-white/[0.04] border border-[#c9a84c]/15 rounded-2xl p-5 hover:bg-[#c9a84c]/[0.07] hover:border-[#c9a84c]/30 transition-colors">
                      <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/35 to-transparent" />
                      <div className="flex items-center gap-2.5 mb-2.5">
                        <span style={cinzel} className="font-black text-[1.3rem] leading-none text-[#c9a84c]/25 select-none flex-shrink-0">{s.num}</span>
                        <div style={cinzel} className="text-[11.5px] font-bold tracking-[0.05em] uppercase text-white/90">{s.title}</div>
                      </div>
                      <p className="text-[12.5px] leading-[1.75] text-white/55">{s.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            BENEFITS (result image + cards)
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection}>
            <Eyebrow align="center">{PAGE.benefits.eyebrow}</Eyebrow>
            <SectionHeading align="center">{PAGE.benefits.heading}</SectionHeading>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-10 items-center">
              <FramedImage src={ResultImg} alt="Hair regrowth result from combined medical therapy" className="h-[260px] sm:h-[320px]" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {PAGE.benefits.items.map((b, i) => (
                  <div key={i} className="bg-white border border-[#c9a84c]/18 rounded-2xl p-5 relative overflow-hidden hover:-translate-y-1 hover:shadow-[0_8px_28px_rgba(90,60,10,0.08)] transition-all duration-300">
                    <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                    <div className="flex items-center gap-2.5 mb-2">
                      <span style={cinzel} className="text-[11px] font-black text-[#c9a84c]">{String(i + 1).padStart(2, "0")}</span>
                      <div style={cinzel} className="text-[11.5px] font-bold tracking-[0.05em] uppercase text-[#1a0f02]">{b.title}</div>
                    </div>
                    <p className="text-[12.5px] leading-[1.75] text-[#6b5535]">{b.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            WHO IS A CANDIDATE (dark band)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow light align="center">{PAGE.candidates.eyebrow}</Eyebrow>
              <SectionHeading light align="center">{PAGE.candidates.heading}</SectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">

                {/* Ideal candidates */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#c9a84c]/[0.07] to-[#c9a84c]/[0.02] border border-[#c9a84c]/18 rounded-2xl p-7">
                  <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7l4 4 6-6" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                    <span style={cinzel} className="text-[12px] font-bold uppercase tracking-[0.06em] text-white/85">Ideal Candidates</span>
                  </div>
                  <ul className="flex flex-col gap-3">
                    {PAGE.candidates.ideal.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[13px] leading-[1.7] text-white/60">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] flex-shrink-0 mt-1.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Not ideal */}
                <div className="relative overflow-hidden bg-white/[0.03] border border-white/10 rounded-2xl p-7">
                  <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-400/30 flex items-center justify-center flex-shrink-0">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 3v5M7 10v1" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round"/></svg>
                    </div>
                    <span style={cinzel} className="text-[12px] font-bold uppercase tracking-[0.06em] text-white/85">May Not Be Suitable</span>
                  </div>
                  <ul className="flex flex-col gap-3">
                    {PAGE.candidates.notIdeal.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[13px] leading-[1.7] text-white/55">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400/60 flex-shrink-0 mt-1.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            FAQs
        ══════════════════════════════════════ */}
        <div className="bg-[#faf8f4] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow align="center">Common Questions</Eyebrow>
              <SectionHeading align="center">Frequently Asked Questions</SectionHeading>
              <div className="max-w-3xl mx-auto flex flex-col gap-3">
                {PAGE.faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            CTA
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="max-w-xl mx-auto text-center">
            <Eyebrow align="center">{PAGE.cta.eyebrow}</Eyebrow>
            <h3 style={cinzel} className="font-black text-[1.3rem] sm:text-[1.65rem] md:text-[1.9rem] text-[#1a0f02] uppercase tracking-[0.06em] mb-3.5">{PAGE.cta.heading}</h3>
            <p className="text-[14px] text-[#6b5535] leading-[1.75] mb-8">{PAGE.cta.sub}</p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <a href={PAGE.cta.primary.href} target="_blank" rel="noopener noreferrer"
                style={{ ...cinzel, backgroundImage: "linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)", backgroundSize: "200% 100%" }}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-3.5 rounded-md text-[#1a0f02] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(201,168,76,0.35)] transition-all inline-block">
                {PAGE.cta.primary.label}
              </a>
              <a href={PAGE.cta.secondary.href} style={cinzel}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-[13px] rounded-md bg-transparent border border-[#1a0f02]/35 text-[#1a0f02] hover:-translate-y-0.5 hover:border-[#c9a84c] hover:text-[#a07828] transition-all inline-block">
                {PAGE.cta.secondary.label}
              </a>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}